import { File } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@tellescope/ui";
import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { formatFileSize } from "@tellescope/utils";
import { TemplateFormDialogContentsSectionForm } from "../types";
import { CarePlanItemPicker } from "./CarePlanItemPicker";
import { CarePlanSelectedList } from "./CarePlanSelectedList";
import { CarePlanFilePreview } from "./CarePlanFilePreview";
import { CarePlanFileItem } from "./CarePlanFileItem";

export interface CarePlanFilesSectionProps {
  form: TemplateFormDialogContentsSectionForm<"carePlanFiles">;
}

export const CarePlanFilesSection = ({ form }: CarePlanFilesSectionProps) => {
  const { t } = useTranslation("calendar");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const filesQuery = useTellescopeInfiniteQuery({
    model: "files",
    method: "getSome",
    request: {
      search: debouncedSearch ? { query: debouncedSearch } : undefined,
    },
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });

  const selectedIds = useMemo(() => new Set(form.values.carePlanFiles ?? []), [form.values.carePlanFiles]);
  const files = useMemo(() => filesQuery.data ?? [], [filesQuery.data]);
  const isLoading = filesQuery.isFetching || search !== debouncedSearch;

  const selectableFiles = useMemo(
    () =>
      files.reduce<TellescopeModelFromName<"files">[]>((acc, curr) => {
        if (selectedIds.has(curr.id)) {
          return acc;
        }
        acc.push(curr);
        return acc;
      }, []),
    [files, selectedIds]
  );

  const handleAdd = (id: string) => {
    if (selectedIds.has(id)) return;
    form.setFieldValue("carePlanFiles", [...selectedIds, id]);
    setSearch("");
    setPopoverOpen(false);
  };

  const handleRemove = (id: string) => {
    form.setFieldValue(
      "carePlanFiles",
      Array.from(selectedIds).filter((f) => f !== id)
    );
  };

  return (
    <div className="space-y-4">
      <CarePlanItemPicker
        icon={<File size={16} />}
        label={t("Files")}
        addButtonText={t("Add File")}
        searchPlaceholder={t("Search files...")}
        emptyText={t("No files found.")}
        searchValue={search}
        onSearchChange={setSearch}
        isLoading={isLoading}
        popoverOpen={popoverOpen}
        onPopoverOpenChange={setPopoverOpen}
        items={selectableFiles}
        getItemId={(f) => f.id}
        onSelectItem={handleAdd}
        renderItem={(f) => (
          <>
            <CarePlanFilePreview file={f} size={24} />
            <span className="truncate flex-1">{f.name}</span>
            <span className="text-xs text-muted-foreground flex-shrink-0">{`(${formatFileSize(f.size)})`}</span>
          </>
        )}
      />
      <CarePlanSelectedList
        ids={form.values.carePlanFiles ?? []}
        emptyText={t("No files selected. Use the dropdown above to add files.")}
        onRemove={handleRemove}
        ItemComponent={CarePlanFileItem}
      />
    </div>
  );
};
